import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import {
    Sparkles, AlertTriangle, MessageSquare, Phone, ArrowRight,
    RefreshCw, Activity, Clock, Inbox,
} from 'lucide-react';
import api, { messagingAPI } from '../../lib/api';
import AnimatedCounter from '../ui/AnimatedCounter';

/**
 * AIPrioritiesPanel — Unified Control Room view for the "Priorités IA" tab.
 *
 * Fetches /cockpit-ai/briefing (silent mandates) + /messaging/ai-actions/dashboard
 * (auto-extracted actions from messaging) in parallel with a 8-second timeout.
 *
 * Merges them into a single priority list sorted by impact, renders
 * explicit empty/error states (no more silent skeletons), Control Room design.
 */
const TIMEOUT_MS = 20000;

const PRIORITY_CONFIG = {
    silent_mandate: { accent: '#FF914D', chip: 'URGENT', icon: Phone, sub: 'Mandat silencieux > 14 j' },
    ai_action_haute: { accent: '#EF4444', chip: 'PRIORITÉ HAUTE', icon: AlertTriangle, sub: 'Extrait de la messagerie' },
    ai_action_moyenne: { accent: '#A855F7', chip: 'À FAIRE', icon: MessageSquare, sub: 'Extrait de la messagerie' },
    ai_action_basse: { accent: '#3B82F6', chip: 'INFO', icon: Activity, sub: 'Extrait de la messagerie' },
};

function withTimeout(promise, ms) {
    let timeoutId;
    const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('timeout')), ms);
    });
    return Promise.race([
        promise.finally(() => clearTimeout(timeoutId)),
        timeout,
    ]);
}

export default function AIPrioritiesPanel({ basePath = '/admin' }) {
    const [briefingState, setBriefingState] = useState({ loading: true, items: [], exposedAmount: 0, error: null });
    const [actionsState, setActionsState] = useState({ loading: true, items: [], error: null });
    const navigate = useNavigate();

    const loadBriefing = useCallback(async () => {
        setBriefingState((s) => ({ ...s, loading: true, error: null }));
        try {
            const res = await withTimeout(api.get('/cockpit-ai/briefing'), TIMEOUT_MS);
            const data = res?.data || {};
            const items = (data.actions || []).map((a, i) => ({
                id: `silent_${i}`,
                type: 'silent_mandate',
                label: a.label,
                sub: a.sub,
                link: a.link,
                score: 100 - i,
            }));
            setBriefingState({ loading: false, items, exposedAmount: data.exposed_amount || 0, error: null });
        } catch (e) {
            setBriefingState((s) => ({
                ...s,
                loading: false,
                error: e?.message === 'timeout' ? 'Délai dépassé · briefing.' : 'Erreur · briefing.',
            }));
        }
    }, []);

    const loadActions = useCallback(async () => {
        setActionsState((s) => ({ ...s, loading: true, error: null }));
        try {
            const res = await withTimeout(messagingAPI.getDashboardActions(), TIMEOUT_MS);
            const list = (res?.data?.actions || []).slice(0, 8).map((a) => {
                const priority = a.priority || 'moyenne';
                return {
                    id: `ai_${a.id}`,
                    type: `ai_action_${priority}`,
                    label: a.title,
                    sub: a.description?.slice(0, 110) + (a.description?.length > 110 ? '…' : ''),
                    link: a.conversation_id ? `${basePath}/messaging?conversation=${a.conversation_id}` : `${basePath}/messaging`,
                    score: priority === 'haute' ? 90 : priority === 'moyenne' ? 50 : 20,
                };
            });
            setActionsState({ loading: false, items: list, error: null });
        } catch (e) {
            setActionsState((s) => ({
                ...s,
                loading: false,
                error: e?.message === 'timeout' ? 'Délai dépassé · actions.' : 'Erreur · actions.',
            }));
        }
    }, [basePath]);

    const reloadAll = useCallback(() => {
        loadBriefing();
        loadActions();
    }, [loadBriefing, loadActions]);

    useEffect(() => { reloadAll(); }, [reloadAll]);

    // Merge items as they arrive (no Promise.all gating)
    const items = [...briefingState.items, ...actionsState.items]
        .sort((a, b) => b.score - a.score)
        .slice(0, 8);

    const stillLoading = briefingState.loading || actionsState.loading;
    const allFailed = !briefingState.loading && !actionsState.loading && briefingState.error && actionsState.error;
    const exposedAmount = briefingState.exposedAmount;

    return (
        <motion.section
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden rounded-2xl border border-foreground/10 bg-foreground/[0.02] backdrop-blur-xl"
            style={{ fontFamily: 'IBM Plex Sans, Manrope, sans-serif' }}
            data-testid="ai-priorities-panel"
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-4 p-5 border-b border-foreground/5">
                <div className="flex items-start gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center shrink-0">
                        <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                        <p
                            className="text-[10px] uppercase tracking-widest text-foreground/40"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            Actions du jour · Powered by Homly AI
                        </p>
                        <h3
                            className="text-lg font-semibold text-foreground tracking-tight"
                            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                        >
                            {stillLoading && items.length === 0
                                ? 'Analyse en cours…'
                                : `${items.length} action${items.length > 1 ? 's' : ''} prioritaire${items.length > 1 ? 's' : ''}`}
                            {!stillLoading && exposedAmount > 0 && (
                                <span className="text-sm text-foreground/50 font-normal ml-3">
                                    ·{' '}
                                    <span
                                        className="text-foreground font-semibold"
                                        style={{ fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
                                    >
                                        <AnimatedCounter value={exposedAmount} suffix=" €" />
                                    </span>{' '}
                                    de pipeline exposé
                                </span>
                            )}
                        </h3>
                    </div>
                </div>
                <button
                    onClick={reloadAll}
                    disabled={stillLoading}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-foreground/5 hover:bg-foreground/10 border border-foreground/10 hover:border-foreground/20 text-xs font-semibold text-foreground/70 hover:text-foreground transition-all disabled:opacity-50"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    data-testid="ai-priorities-refresh"
                >
                    <RefreshCw className={`w-3 h-3 ${stillLoading ? 'animate-spin' : ''}`} />
                    Actualiser
                </button>
            </div>

            {/* Content */}
            <div className="p-5">
                {items.length === 0 && stillLoading ? (
                    <LoadingState />
                ) : items.length === 0 && allFailed ? (
                    <ErrorState message="Impossible de joindre les services IA pour le moment." onRetry={reloadAll} />
                ) : items.length === 0 ? (
                    <EmptyState />
                ) : (
                    <div className="space-y-2.5">
                        {items.map((item, i) => (
                            <PriorityRow
                                key={item.id}
                                item={item}
                                index={i}
                                onClick={() => item.link && navigate(item.link)}
                            />
                        ))}
                        {stillLoading && (
                            <div
                                className="text-[11px] text-foreground/40 text-center py-2"
                                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                                data-testid="ai-priorities-partial-loading"
                            >
                                <RefreshCw className="w-3 h-3 inline-block mr-1.5 animate-spin" />
                                Chargement des autres sources…
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer signature */}
            <div className="px-5 py-3 border-t border-foreground/5 flex items-center gap-2 text-[11px] text-foreground/40">
                <Clock className="w-3 h-3 text-[#FF914D]" />
                <span style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    Analyse temps réel · Refresh manuel ou auto au prochain rendu
                </span>
            </div>
        </motion.section>
    );
}

/* ──────────── Sub-components ──────────── */

function PriorityRow({ item, index, onClick }) {
    const cfg = PRIORITY_CONFIG[item.type] || PRIORITY_CONFIG.ai_action_moyenne;
    const Icon = cfg.icon || Activity;
    const LucIcon = LucideIcons[cfg.iconName] || Icon;
    return (
        <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.06, duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            onClick={onClick}
            className="group w-full text-left flex items-start gap-3 p-3.5 rounded-xl bg-foreground/[0.02] border border-foreground/8 hover:border-foreground/20 hover:bg-foreground/[0.05] transition-all"
            style={{ borderLeft: `3px solid ${cfg.accent}` }}
            data-testid={`ai-priority-row-${index}`}
        >
            <div
                className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${cfg.accent}1a`, border: `1px solid ${cfg.accent}33` }}
            >
                <LucIcon className="w-4 h-4" style={{ color: cfg.accent }} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span
                        className="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-bold tracking-wider border"
                        style={{
                            fontFamily: 'Space Grotesk, sans-serif',
                            background: `${cfg.accent}26`,
                            color: cfg.accent,
                            borderColor: `${cfg.accent}55`,
                        }}
                    >
                        {cfg.chip}
                    </span>
                    <span
                        className="text-[10px] text-foreground/30"
                        style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                    >
                        {cfg.sub}
                    </span>
                </div>
                <p
                    className="text-sm font-semibold text-foreground leading-snug truncate"
                    style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                    {item.label}
                </p>
                {item.sub && (
                    <p
                        className="text-xs text-foreground/50 leading-relaxed mt-0.5 truncate"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                        {item.sub}
                    </p>
                )}
            </div>
            <ArrowRight
                className="w-4 h-4 text-foreground/30 group-hover:translate-x-0.5 transition-transform shrink-0"
                style={{ color: undefined }}
            />
        </motion.button>
    );
}

function LoadingState() {
    return (
        <div className="space-y-2.5" data-testid="ai-priorities-loading">
            {[0, 1, 2].map((i) => (
                <div
                    key={`pri-skel-${i}`}
                    className="h-16 rounded-xl bg-foreground/[0.03] border border-foreground/5 animate-pulse"
                />
            ))}
        </div>
    );
}

function ErrorState({ message, onRetry }) {
    return (
        <div
            className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-5 text-center"
            data-testid="ai-priorities-error"
        >
            <AlertTriangle className="w-6 h-6 text-amber-400 mx-auto mb-2" />
            <p className="text-sm text-amber-300 mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {message}
            </p>
            <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/30 text-xs font-semibold text-amber-300 transition-all"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                data-testid="ai-priorities-retry"
            >
                <RefreshCw className="w-3 h-3" />
                Réessayer
            </button>
        </div>
    );
}

function EmptyState() {
    return (
        <div
            className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-6 text-center"
            data-testid="ai-priorities-empty"
        >
            <Inbox className="w-7 h-7 text-emerald-400 mx-auto mb-2" />
            <p
                className="text-sm font-semibold text-emerald-300"
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
                Aucune action prioritaire — beau travail !
            </p>
            <p
                className="text-xs text-foreground/40 mt-1"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
                Tous vos mandats sont suivis et la messagerie est à jour.
            </p>
        </div>
    );
}
